import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, ChevronUp, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating "what's fresh" pill rendered in the bottom-right corner.
// Shows compact "v33 · Updated May 2" text; on hover (desktop) or tap
// (mobile) expands into a popover listing the latest in-progress
// reconstruction milestones with their dates and progress bars.
//
// Polls /api/app/version once on mount and every 5 minutes after, so the
// content stays current for stakeholders who keep the page open all day.
export default function FreshnessPill() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [installBannerVisible, setInstallBannerVisible] = useState(false);
  const popoverRef = useRef(null);
  const pillRef = useRef(null);

  // Detect the PWA install banner so we can lift the pill above it. The
  // banner is centered+92vw wide on mobile, which overlaps a right-anchored
  // pill at bottom-4. We bump the pill to bottom-24 while the banner is open.
  useEffect(() => {
    const check = () => setInstallBannerVisible(!!document.querySelector('[data-testid="install-banner"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  const fetchVersion = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/app/version`);
      setData(d);
    } catch (e) {
      // Silently fail — pill just stays hidden if endpoint unavailable.
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    const id = setInterval(fetchVersion, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchVersion]);

  // Click-outside to close the popover (mobile tap-toggle).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (pillRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  if (!data) return null;

  const versionShort = (data.version || '').replace(/^atrium-/, '');
  const updatedDate = data.last_updated_at ? new Date(data.last_updated_at) : null;
  const updatedLabel = updatedDate
    ? updatedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '—';
  const milestones = data.latest_milestones || [];

  return (
    <>
      {/* Pill */}
      <button
        ref={pillRef}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        data-testid="freshness-pill"
        title="Reconstruction freshness — click for latest milestones"
        className="fixed right-4 z-[60] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all hover:-translate-y-0.5"
        style={{
          bottom: installBannerVisible ? '6.5rem' : '1rem',
          backgroundColor: 'rgba(10, 26, 47, 0.92)',
          color: 'white',
          boxShadow: '0 4px 14px rgba(10, 26, 47, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          fontFamily: 'Manrope, sans-serif',
          transition: 'bottom 240ms ease, transform 200ms ease',
        }}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: '#22C55E', boxShadow: '0 0 8px #22C55E' }}
          aria-hidden
        />
        <span style={{ color: '#A3D4D4' }}>{versionShort}</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>Updated {updatedLabel}</span>
        <ChevronUp className="w-3 h-3" style={{ opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
      </button>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          data-testid="freshness-popover"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="fixed right-4 z-[60] rounded-2xl overflow-hidden"
          style={{
            bottom: installBannerVisible ? '10rem' : '3.5rem',
            width: 'min(340px, calc(100vw - 32px))',
            backgroundColor: 'white',
            boxShadow: '0 12px 36px rgba(10, 26, 47, 0.28), 0 0 0 1px rgba(0, 61, 165, 0.12)',
            animation: 'freshness-pop 220ms cubic-bezier(0.2, 0.9, 0.3, 1.2)',
            transition: 'bottom 240ms ease',
          }}
        >
          {/* Header */}
          <div
            className="px-4 py-2.5 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" style={{ color: '#F4E4BC' }} />
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: 'white', fontFamily: 'Manrope, sans-serif' }}>
                Latest Milestones
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="sm:hidden w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
              aria-label="Close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Milestone list */}
          <div className="p-3 max-h-[60vh] overflow-y-auto">
            {milestones.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: '#7A8C8C', fontFamily: 'Manrope, sans-serif' }}>
                All milestones complete — nothing in flight right now.
              </p>
            ) : (
              <div className="space-y-2.5">
                {milestones.map((m) => (
                  <MilestoneRow key={m.id} milestone={m} />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
            <span className="text-[9px] font-bold tracking-wider uppercase" style={{ color: '#7A8C8C', fontFamily: 'Manrope, sans-serif' }}>
              Build {versionShort}
            </span>
            <span className="text-[9px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope, sans-serif' }}>
              Last activity {updatedDate ? updatedDate.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
            </span>
          </div>
        </div>
      )}

      <style>{`@keyframes freshness-pop {
        0% { opacity: 0; transform: translateY(8px) scale(0.96); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }`}</style>
    </>
  );
}

function MilestoneRow({ milestone }) {
  const m = milestone;
  const start = m.start_date ? new Date(m.start_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
  const end = m.end_date ? new Date(m.end_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
  const dateLabel = start && end ? `${start} → ${end}` : (start || end || '—');
  return (
    <div className="flex items-start gap-2.5">
      <span
        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
        style={{ backgroundColor: m.color, boxShadow: `0 0 8px ${m.color}80` }}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope, sans-serif' }}>
            {m.name}
          </p>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: m.color, fontFamily: 'Manrope, sans-serif' }}>
            {m.progress_percent}%
          </span>
        </div>
        <p className="text-[10px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope, sans-serif' }}>
          {dateLabel}{m.events_total ? ` · ${m.events_total} task${m.events_total === 1 ? '' : 's'}` : ''}
        </p>
        {/* Progress bar */}
        <div className="mt-1 w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#F1F5F9' }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(2, m.progress_percent)}%`, backgroundColor: m.color }}
          />
        </div>
      </div>
    </div>
  );
}
